import React, { useState, useEffect } from 'react';
import {
  Button,
  Table,
  message,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Card,
  Space,
  Tag,
  Avatar,
  Tooltip,
  Empty,
  Typography,
  Row,
  Col,
  Spin,
  Statistic,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  ContactsOutlined,
  EditOutlined,
  TeamOutlined,
  IdcardOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const { Option } = Select;
const { Text } = Typography;

const SupplierContacts = () => {
  const { t } = useTranslation();

  const [contacts, setContacts] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [supplierInfo, setSupplierInfo] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const user = JSON.parse(localStorage.getItem('user'));
  const supplierId = user?.supplierId;
  const token = localStorage.getItem('urlToken');

  // Contact type colors
  const getContactTypeColor = (type) => {
    const colors = {
      Manager: '#1890ff',
      Technical: '#52c41a',
      Sales: '#fa8c16',
      Support: '#722ed1',
      Finance: '#eb2f96',
      Quality: '#13c2c2',
      Logistics: '#faad14',
      Default: '#8c8c8c',
    };
    return colors[type] || colors['Default'];
  };

  // ✅ Fetch Contact Types
  const fetchContactTypes = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/SupplierContact/types`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.types) {
        setContactTypes(response.data.types);
      } else {
        setContactTypes([]);
      }
    } catch (error) {
      console.error('Contact types fetch error:', error);
      message.error(t('fetch_contact_types_error'));
      setContactTypes([]);
    }
  };

  // ✅ Fetch Contacts
  const fetchContacts = async () => {
    if (!supplierId) {
      message.error(t('no_supplier_id_found'));
      return;
    }

    setTableLoading(true);
    try {
      const response = await axios.get(
        `${apiUrl}/api/SupplierContact/supplier/${supplierId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        setSupplierInfo({
          supplierCode: response.data.supplierCode,
          supplierName: response.data.supplierName,
          contactCount: response.data.contactCount,
        });

        if (response.data.contacts && Array.isArray(response.data.contacts)) {
          setContacts(response.data.contacts);
        } else {
          setContacts([]);
        }
      }
    } catch (error) {
      console.error('Contacts fetch error:', error);
      setContacts([]);
      if (error.response?.status === 404) {
        message.info(t('no_contacts_found'));
      } else {
        message.error(t('fetch_contacts_error'));
      }
    } finally {
      setTableLoading(false);
    }
  };

  // ✅ Add/Update Contact
  const handleAddContact = async (values) => {
    setLoading(true);
    try {
      const payload = {
        supplierId: supplierId,
        contactTypeId: values.contactTypeId,
        name: values.name,
        phone: values.phone,
        email: values.email,
      };

      if (editingContact) {
        await axios.put(
          `${apiUrl}/api/SupplierContact/${editingContact.contactId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        message.success(t('contact_updated_successfully'));
      } else {
        await axios.post(`${apiUrl}/api/SupplierContact`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        message.success(t('contact_added_successfully'));
      }

      handleModalClose();
      fetchContacts();
    } catch (error) {
      console.error('Add/Update contact error:', error);
      if (error.response?.status === 409) {
        message.error(t('email_already_exists'));
      } else {
        message.error(
          editingContact
            ? t('failed_to_update_contact')
            : t('failed_to_add_contact')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Edit Contact
  const handleEditContact = (contact) => {
    setEditingContact(contact);
    form.setFieldsValue({
      name: contact.name,
      phone: contact.phone,
      email: contact.email,
      contactTypeId: contact.contactTypeId,
    });
    setIsModalVisible(true);
  };

  // ✅ Delete Contact
  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${apiUrl}/api/SupplierContact/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success(t('contact_deleted_successfully'));
      fetchContacts();
    } catch (error) {
      console.error('Delete contact error:', error);
      message.error(t('failed_to_delete_contact'));
    }
  };

  // ✅ Close Modal
  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingContact(null);
    form.resetFields();
  };

  useEffect(() => {
    if (!supplierId || !token) {
      message.error(t('no_supplier_id_found'));
      return;
    }
    fetchContactTypes();
    fetchContacts();
  }, [supplierId, token]);

  // Statistics
  const totalContacts = contacts.length;
  const managerContacts = contacts.filter(c => c.contactTypeName === 'Manager').length;
  const technicalContacts = contacts.filter(c => c.contactTypeName === 'Technical').length;
  const otherContacts = totalContacts - managerContacts - technicalContacts;

  // ✅ Table Columns
  const columns = [
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('contact_info')}</span>,
      key: 'contactInfo',
      render: (_, record) => (
        <Space size={12}>
          <Avatar
            size={36}
            style={{
              backgroundColor: getContactTypeColor(record.contactTypeName),
              fontSize: '14px',
            }}
          >
            {record.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: '13px', color: '#262626', display: 'block' }}>
              {record.name}
            </Text>
            <Tag
              color={getContactTypeColor(record.contactTypeName)}
              style={{ fontSize: '11px', marginTop: '2px' }}
            >
              {record.contactTypeName}
            </Tag>
          </div>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('phone_number')}</span>,
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => (
        <Space size={6}>
          <PhoneOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
          <Text copyable={{ text: phone }} style={{ fontSize: '13px', color: '#595959' }}>
            {phone || '-'}
          </Text>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('email_address')}</span>,
      dataIndex: 'email',
      key: 'email',
      render: (email) => (
        <Space size={6}>
          <MailOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
          <Text 
            copyable={{ text: email }} 
            style={{ fontSize: '13px', color: '#1890ff' }}
          >
            {email || '-'}
          </Text>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('last_update')}</span>,
      dataIndex: 'lastUpdateDate',
      key: 'lastUpdateDate',
      width: 140,
      render: (date) => (
        <div>
          <Text style={{ fontSize: '13px', color: '#595959', display: 'block' }}>
            {moment(date).format('DD/MM/YYYY')}
          </Text>
          <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
            {moment(date).format('HH:mm')}
          </Text>
        </div>
      ),
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('actions')}</span>,
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditContact(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Popconfirm
            title={t('delete_contact')}
            description={t('delete_confirmation')}
            onConfirm={() => handleDeleteContact(record.contactId)}
            okText={t('yes')}
            cancelText={t('no')}
            okButtonProps={{ danger: true }}
          >
            <Tooltip title={t('delete')}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Spin spinning={tableLoading}>
        {/* Page Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#262626' }}>
            {t('supplier_contacts')}
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#8c8c8c', fontSize: '14px' }}>
            {t('manage_your_contact_information')}
          </p>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ borderRadius: '8px' }}>
              <Statistic
                title={t('total_contacts')}
                value={totalContacts}
                prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ fontSize: '28px', fontWeight: '600', color: '#262626' }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ borderRadius: '8px' }}>
              <Statistic
                title={t('manager_contacts')}
                value={managerContacts}
                prefix={<SafetyOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ fontSize: '28px', fontWeight: '600', color: '#262626' }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ borderRadius: '8px' }}>
              <Statistic
                title={t('technical_contacts')}
                value={technicalContacts}
                prefix={<ContactsOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ fontSize: '28px', fontWeight: '600', color: '#262626' }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ borderRadius: '8px' }}>
              <Statistic
                title={t('other_contacts')}
                value={otherContacts}
                prefix={<UserOutlined style={{ color: '#722ed1' }} />}
                valueStyle={{ fontSize: '28px', fontWeight: '600', color: '#262626' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Company Info Card */}
        {supplierInfo && (
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col span={24}>
              <Card
                bordered={false}
                style={{ borderRadius: '8px' }}
                bodyStyle={{ padding: '20px 24px' }}
              >
                <Row gutter={[24, 16]} align="middle">
                  <Col xs={24} md={8}>
                    <Space size={12}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#e6f7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <IdcardOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          {t('company_name')}
                        </Text>
                        <Text strong style={{ fontSize: '14px' }}>
                          {supplierInfo.supplierName}
                        </Text>
                      </div>
                    </Space>
                  </Col>

                  <Col xs={24} md={8}>
                    <Space size={12}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#f6ffed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <SafetyOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          {t('supplier_code')}
                        </Text>
                        <Text strong style={{ fontSize: '14px' }}>
                          {supplierInfo.supplierCode}
                        </Text>
                      </div>
                    </Space>
                  </Col>

                  <Col xs={24} md={8}>
                    <Space size={12}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#fff7e6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <ContactsOutlined style={{ fontSize: '20px', color: '#faad14' }} />
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                          {t('total_contacts')}
                        </Text>
                        <Text strong style={{ fontSize: '14px' }}>
                          {supplierInfo.contactCount} {t('contacts')}
                        </Text>
                      </div>
                    </Space>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        )}

        {/* Main Table Card */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                    <TeamOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    {t('contact_list')}
                  </span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsModalVisible(true)}
                    style={{
                      borderRadius: '8px',
                      height: '40px',
                      fontWeight: '500',
                    }}
                  >
                    {t('add_new_contact')}
                  </Button>
                </div>
              }
              bordered={false}
              style={{ borderRadius: '8px' }}
              bodyStyle={{ padding: '16px' }}
            >
              <Table
                dataSource={contacts}
                columns={columns}
                rowKey="contactId"
                pagination={{
                  pageSize: 8,
                  showSizeChanger: false,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} ${t('of')} ${total} ${t('contacts')}`,
                  style: { marginTop: '16px', marginBottom: 0 },
                  size: 'small',
                }}
                size="small"
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <div>
                          <Text type="secondary" style={{ fontSize: '13px' }}>
                            {t('no_contacts_found')}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {t('click_add_button_to_create_first_contact')}
                          </Text>
                        </div>
                      }
                      style={{ padding: '40px 0' }}
                    />
                  ),
                }}
              />
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <Space size={12}>
            <UserOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
            <span style={{ fontSize: '16px', fontWeight: '600' }}>
              {editingContact ? t('edit_contact') : t('add_new_contact')}
            </span>
          </Space>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        centered
        bodyStyle={{ padding: '24px' }}
      >
        <Form form={form} layout="vertical" onFinish={handleAddContact}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={
                  <Text strong style={{ fontSize: '13px' }}>
                    {t('full_name')}
                  </Text>
                }
                rules={[{ required: true, message: t('please_input_name') }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder={t('enter_full_name')}
                  size="large"
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contactTypeId"
                label={
                  <Text strong style={{ fontSize: '13px' }}>
                    {t('contact_type')}
                  </Text>
                }
                rules={[
                  { required: true, message: t('please_select_contact_type') },
                ]}
              >
                <Select
                  placeholder={t('select_contact_type')}
                  size="large"
                  style={{ borderRadius: '8px' }}
                >
                  {contactTypes.map((type) => (
                    <Option key={type.contactTypeId} value={type.contactTypeId}>
                      <Space size={8}>
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: getContactTypeColor(type.name),
                          }}
                        />
                        <span>{type.name}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label={
                  <Text strong style={{ fontSize: '13px' }}>
                    {t('phone_number')}
                  </Text>
                }
                rules={[{ required: true, message: t('please_input_phone') }]}
              >
                <Input
                  prefix={<PhoneOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder={t('enter_phone_number')}
                  size="large"
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label={
                  <Text strong style={{ fontSize: '13px' }}>
                    {t('email_address')}
                  </Text>
                }
                rules={[
                  { required: true, message: t('please_input_email') },
                  { type: 'email', message: t('please_enter_valid_email') },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder={t('enter_email_address')}
                  size="large"
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px', textAlign: 'right' }}>
            <Space size="middle">
              <Button
                onClick={handleModalClose}
                style={{
                  borderRadius: '8px',
                  height: '40px',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  borderRadius: '8px',
                  height: '40px',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  fontWeight: '500',
                }}
              >
                {editingContact ? t('update') : t('submit')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierContacts;
